import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Target, Rocket, Users, CheckCircle, BarChart3, Zap, TrendingUp, Clock, Shield, Award, Layers, Focus, Mountain, ListChecks, Gauge, Phone, MessageSquare, Star } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "Getting a Grip on",
      highlight: "Your Business",
      subhead: "The EOS Way",
      tagline: "How Traction Principles Drive Our Scorecard Success"
    }
  },
  {
    id: 2,
    type: "intro",
    content: {
      label: "THE BOOK",
      headline: "What is Traction?",
      description: "Traction by Gino Wickman introduces the Entrepreneurial Operating System (EOS) - a practical method for achieving business success.",
      points: [
        "A complete system for running your business",
        "Simple tools anyone can implement",
        "Proven by thousands of companies",
        "Focus on what matters most"
      ],
      quote: "Most entrepreneurs are running their businesses by the seat of their pants."
    }
  },
  {
    id: 3,
    type: "sixComponents",
    content: {
      label: "THE EOS MODEL",
      headline: "Six Key Components",
      components: [
        { name: "Vision", desc: "Where are we going?", icon: "target", color: "#22d3ee" },
        { name: "People", desc: "Right people, right seats", icon: "users", color: "#4ade80" },
        { name: "Data", desc: "Scorecard & measurables", icon: "chart", color: "#f97316" },
        { name: "Issues", desc: "Identify & solve", icon: "check", color: "#a855f7" },
        { name: "Process", desc: "Document & follow", icon: "layers", color: "#ec4899" },
        { name: "Traction", desc: "Rocks & accountability", icon: "rocket", color: "#eab308" },
      ],
      quote: "Strengthen all six and watch your business transform."
    }
  },
  {
    id: 4,
    type: "rocks",
    content: {
      label: "THE POWER OF ROCKS",
      headline: "What Are Rocks?",
      definition: "Rocks are the 3-7 most important priorities that must be accomplished in the next 90 days.",
      principles: [
        { title: "Quarterly Focus (or Monthly)", desc: "90-day or 30-day sprints keep teams aligned and accountable" },
        { title: "3-7 Per Person", desc: "Less is more - focus on what truly moves the needle" },
        { title: "SMART Goals", desc: "Specific, Measurable, Attainable, Relevant, Time-bound" },
        { title: "Weekly Check-ins", desc: "On track or off track - simple accountability" },
      ],
      quote: "If everything is important, then nothing is."
    }
  },
  {
    id: 5,
    type: "bigRocks",
    content: {
      label: "THE BIG ROCKS STORY",
      headline: "Prioritize What Matters",
      story: "A professor filled a jar with big rocks and asked if it was full. Students said yes. He added pebbles, then sand, then water.",
      lesson: "If you don't put the big rocks in first, you'll never fit them in at all.",
      visual: [
        { size: "large", label: "Rocks", desc: "Critical priorities", color: "#f97316" },
        { size: "medium", label: "Pebbles", desc: "Important tasks", color: "#3b82f6" },
        { size: "small", label: "Sand", desc: "Day-to-day work", color: "#8b5cf6" },
      ],
      quote: "Start with your rocks. Everything else will fit around them."
    }
  },
  {
    id: 6,
    type: "scorecard",
    content: {
      label: "THE SCORECARD",
      headline: "Managing by Data",
      description: "A weekly Scorecard gives you a pulse on your business with 5-15 high-level numbers.",
      benefits: [
        { icon: "gauge", label: "Pulse Check", desc: "Know your business health at a glance" },
        { icon: "trending", label: "Spot Trends", desc: "See problems before they become crises" },
        { icon: "target", label: "Stay Focused", desc: "Track only what truly matters" },
        { icon: "users", label: "Accountability", desc: "Everyone owns their numbers" },
      ],
      quote: "What gets measured gets managed."
    }
  },
  {
    id: 7,
    type: "whyBuilt",
    content: {
      label: "WHY WE BUILT THIS",
      headline: "From Theory to Practice",
      useLogo: true,
      reasons: [
        "We needed a way to track our Rocks quarterly",
        "Spreadsheets weren't cutting it anymore",
        "We wanted real-time visibility into team performance",
        "Everyone needed to see how their work contributes",
        "Accountability requires transparency"
      ],
      result: "So we built the Calldrip Scorecard to live and breathe Traction every day.",
      quote: "We don't just sell performance - we live it."
    }
  },
  {
    id: 8,
    type: "teamKPIs",
    content: {
      label: "TEAM SCORECARDS",
      headline: "KPIs That Matter",
      note: "These KPIs are a starting point and may evolve as we refine what drives results.",
      salesNote: "On the Sales scorecard, we'll be tracking outbound activity, demos booked, and pipeline progression.",
      teams: [
        { 
          name: "Sales", 
          color: "#3b82f6",
          kpis: ["Calls Made", "Demos Booked", "Deals Closed", "Pipeline Value", "Conversion Rate"]
        },
        { 
          name: "Customer Success", 
          color: "#10b981",
          kpis: ["Onboardings Completed", "NPS Score", "Retention Rate", "Support Tickets", "Upsells"]
        },
        { 
          name: "Development", 
          color: "#8b5cf6",
          kpis: ["Features Shipped", "Bugs Fixed", "Sprint Velocity", "Code Quality", "Uptime"]
        },
      ],
      quote: "Each team has their own rocks. Each team owns their scorecard."
    }
  },
  {
    id: 9,
    type: "teamKPIs2",
    content: {
      label: "TEAM SCORECARDS",
      headline: "Every Department Aligned",
      teams: [
        { 
          name: "Support", 
          color: "#f97316",
          kpis: ["Response Time", "Resolution Rate", "Customer Satisfaction", "Tickets Closed", "Escalations"]
        },
        { 
          name: "Marketing", 
          color: "#ec4899",
          kpis: ["Leads Generated", "Website Traffic", "Conversion Rate", "Brand Awareness", "Campaign ROI"]
        },
      ],
      quote: "When everyone knows their number, everyone rows in the same direction."
    }
  },
  {
    id: 10,
    type: "level10",
    content: {
      label: "WEEKLY RHYTHM",
      headline: "The Calldrip Ops Meeting",
      description: "Every Monday morning, we run a 30-minute manager meeting to stay aligned and address issues.",
      agenda: [
        { time: "2 min", item: "Wins - Share team victories" },
        { time: "3 min", item: "Scorecard Review" },
        { time: "3 min", item: "Rock Review" },
        { time: "2 min", item: "Customer/Employee Headlines" },
        { time: "5 min", item: "To-Do List Review" },
        { time: "12 min", item: "IDS - Identify, Discuss, Solve" },
        { time: "3 min", item: "Conclude - Recap & Action Items" },
      ],
      quote: "Same day. Same time. Every week. No excuses."
    }
  },
  {
    id: 11,
    type: "ids",
    content: {
      label: "SOLVE ISSUES",
      headline: "IDS: Identify, Discuss, Solve",
      description: "The EOS approach to tackling problems head-on.",
      steps: [
        { letter: "I", word: "Identify", desc: "Get to the root cause, not symptoms", color: "#3b82f6" },
        { letter: "D", word: "Discuss", desc: "Everyone contributes, no politics", color: "#10b981" },
        { letter: "S", word: "Solve", desc: "Decide and assign action items", color: "#f97316" },
      ],
      quote: "Most issues aren't solved because they're never truly identified."
    }
  },
  {
    id: 12,
    type: "accountability",
    content: {
      label: "ACCOUNTABILITY",
      headline: "Rocks + Scorecard = Results",
      equation: [
        { label: "Quarterly Rocks", sublabel: "Monthly Rocks", icon: "mountain" },
        { label: "+", type: "operator" },
        { label: "Weekly Scorecard", icon: "chart" },
        { label: "+", type: "operator" },
        { label: "Calldrip Ops Meeting", icon: "users" },
        { label: "=", type: "operator" },
        { label: "Traction", icon: "rocket" },
      ],
      message: "This is the Calldrip vision for the future. Accountability drives results.",
      quote: "Accountability isn't punishment. It's clarity."
    }
  },
  {
    id: 13,
    type: "ourRocks",
    content: {
      label: "Q1 2026 COMPANY ROCKS",
      headline: "What We're Focused On",
      rocks: [
        { title: "Launch Scorecard V2", owner: "Dev Team", status: "on-track" },
        { title: "50 Demos After NADA", owner: "Sales", status: "on-track" },
        { title: "Achieve 95% Uptime", owner: "Engineering", status: "on-track" },
        { title: "Reduce Churn to < 3%", owner: "Customer Success", status: "at-risk" },
        { title: "Complete EOS Implementation", owner: "Leadership", status: "on-track" },
      ],
      quote: "Transparency breeds trust. Here are our rocks."
    }
  },
  {
    id: 14,
    type: "takeaway",
    content: {
      label: "KEY TAKEAWAYS",
      headline: "Start Getting Traction",
      takeaways: [
        { icon: "target", text: "Set 3-7 Rocks each quarter (or month)" },
        { icon: "chart", text: "Track 5-15 numbers weekly" },
        { icon: "users", text: "Run Calldrip Ops Meeting religiously" },
        { icon: "check", text: "Use IDS to solve real issues" },
        { icon: "rocket", text: "Stay focused on what matters" },
      ],
      quote: "Vision without traction is merely hallucination. - Gino Wickman"
    }
  },
  {
    id: 15,
    type: "cta",
    content: {
      headline: "Let's Get",
      highlight: "Traction",
      subhead: "Together",
      message: "The Calldrip Scorecard was built on these principles. Now let's use them to drive your success.",
      useLogo: true,
      actions: [
        "Review your team's Rocks",
        "Check your Scorecard numbers",
        "Come prepared for Ops Meeting"
      ]
    }
  }
];

// Icon component mapper
const IconComponent = ({ name, size = 24, className = "" }) => {
  const icons = {
    target: Target,
    rocket: Rocket,
    users: Users,
    check: CheckCircle,
    chart: BarChart3,
    zap: Zap,
    trending: TrendingUp,
    clock: Clock,
    shield: Shield,
    award: Award,
    layers: Layers,
    focus: Focus,
    mountain: Mountain,
    list: ListChecks,
    gauge: Gauge,
    phone: Phone,
    message: MessageSquare,
    star: Star,
  };
  const Icon = icons[name] || Target;
  return <Icon size={size} className={className} />;
};

// Slide renderers
const TitleSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-12 opacity-90" />
    <h1 className="text-5xl md:text-7xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-7xl font-bold text-orange-500 mb-4">
      {content.highlight}
    </h1>
    <h2 className="text-3xl md:text-4xl font-light text-white/90 mb-8">
      {content.subhead}
    </h2>
    <p className="text-xl text-cyan-400 max-w-2xl">{content.tagline}</p>
  </div>
);

const IntroSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{content.headline}</h2>
    <p className="text-xl text-white/80 mb-8 max-w-3xl">{content.description}</p>
    <div className="grid grid-cols-2 gap-4 mb-8">
      {content.points.map((point, i) => (
        <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-4">
          <CheckCircle className="text-green-400 flex-shrink-0" size={24} />
          <span className="text-white/90">{point}</span>
        </div>
      ))}
    </div>
    <div className="mt-auto">
      <p className="text-cyan-400 text-lg italic">"{content.quote}"</p>
    </div>
  </div>
);

const SixComponentsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="grid grid-cols-3 gap-6 flex-1">
      {content.components.map((comp, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 flex flex-col items-center text-center border border-white/10 hover:border-white/30 transition-all">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: comp.color + "20" }}>
            <IconComponent name={comp.icon} size={32} className="text-white" style={{ color: comp.color }} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{comp.name}</h3>
          <p className="text-white/60 text-sm">{comp.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const RocksSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.headline}</h2>
    <p className="text-xl text-cyan-400 mb-8">{content.definition}</p>
    <div className="grid grid-cols-2 gap-6 flex-1">
      {content.principles.map((principle, i) => (
        <div key={i} className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-6 border border-orange-500/30">
          <h3 className="text-xl font-bold text-orange-400 mb-2">{principle.title}</h3>
          <p className="text-white/80">{principle.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const BigRocksSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{content.headline}</h2>
    <p className="text-lg text-white/80 mb-4">{content.story}</p>
    <p className="text-xl text-cyan-400 font-semibold mb-8">{content.lesson}</p>
    <div className="flex items-end justify-center gap-8 flex-1 pb-8">
      {content.visual.map((item, i) => (
        <div key={i} className="flex flex-col items-center">
          <div 
            className={`rounded-lg flex items-center justify-center mb-3 ${
              item.size === 'large' ? 'w-32 h-32' : item.size === 'medium' ? 'w-24 h-24' : 'w-16 h-16'
            }`}
            style={{ backgroundColor: item.color }}
          >
            <Mountain size={item.size === 'large' ? 48 : item.size === 'medium' ? 32 : 20} className="text-white" />
          </div>
          <h4 className="text-lg font-bold text-white">{item.label}</h4>
          <p className="text-white/60 text-sm">{item.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic">"{content.quote}"</p>
  </div>
);

const ScorecardSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.headline}</h2>
    <p className="text-lg text-white/80 mb-8">{content.description}</p>
    <div className="grid grid-cols-2 gap-6 flex-1">
      {content.benefits.map((benefit, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 flex items-start gap-4 border border-white/10">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <IconComponent name={benefit.icon} size={24} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white mb-1">{benefit.label}</h3>
            <p className="text-white/60">{benefit.desc}</p>
          </div>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const WhyBuiltSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <div className="flex items-center gap-4 mb-6">
      <h2 className="text-4xl md:text-5xl font-bold text-white">{content.headline}</h2>
      {content.useLogo && <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-8 opacity-80" />}
    </div>
    <div className="space-y-4 mb-8">
      {content.reasons.map((reason, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-lg p-4">
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">{i + 1}</div>
          <span className="text-white/90 text-lg">{reason}</span>
        </div>
      ))}
    </div>
    <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-6 border border-cyan-500/30">
      <p className="text-xl text-white font-semibold">{content.result}</p>
    </div>
    <p className="text-cyan-400 text-lg italic mt-auto pt-6">"{content.quote}"</p>
  </div>
);

const TeamKPIsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.headline}</h2>
    {content.note && (
      <p className="text-yellow-400/90 text-sm mb-2 italic">{content.note}</p>
    )}
    {content.salesNote && (
      <p className="text-cyan-400/90 text-sm mb-6">{content.salesNote}</p>
    )}
    <div className="grid grid-cols-3 gap-6 flex-1">
      {content.teams.map((team, i) => (
        <div key={i} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
          <div className="p-4" style={{ backgroundColor: team.color }}>
            <h3 className="text-xl font-bold text-white">{team.name}</h3>
          </div>
          <div className="p-4 space-y-2">
            {team.kpis.map((kpi, j) => (
              <div key={j} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: team.color }} />
                <span className="text-white/80 text-sm">{kpi}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const TeamKPIs2Slide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="grid grid-cols-2 gap-8 flex-1 max-w-4xl mx-auto w-full">
      {content.teams.map((team, i) => (
        <div key={i} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
          <div className="p-4" style={{ backgroundColor: team.color }}>
            <h3 className="text-xl font-bold text-white">{team.name}</h3>
          </div>
          <div className="p-4 space-y-2">
            {team.kpis.map((kpi, j) => (
              <div key={j} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: team.color }} />
                <span className="text-white/80">{kpi}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const Level10Slide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.headline}</h2>
    <p className="text-lg text-white/80 mb-6">{content.description}</p>
    <div className="bg-white/5 rounded-xl p-6 border border-white/10 flex-1">
      <div className="space-y-3">
        {content.agenda.map((item, i) => (
          <div key={i} className={`flex items-center gap-4 p-3 rounded-lg ${i === 5 ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-white/5'}`}>
            <div className={`px-3 py-1 rounded font-mono text-sm ${i === 5 ? 'bg-orange-500 text-white' : 'bg-cyan-500/20 text-cyan-400'}`}>
              {item.time}
            </div>
            <span className={`${i === 5 ? 'text-orange-400 font-semibold' : 'text-white/80'}`}>{item.item}</span>
          </div>
        ))}
      </div>
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const IDSSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.headline}</h2>
    <p className="text-lg text-white/80 mb-8">{content.description}</p>
    <div className="flex items-center justify-center gap-8 flex-1">
      {content.steps.map((step, i) => (
        <div key={i} className="flex flex-col items-center">
          <div 
            className="w-24 h-24 rounded-full flex items-center justify-center text-5xl font-bold text-white mb-4"
            style={{ backgroundColor: step.color }}
          >
            {step.letter}
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">{step.word}</h3>
          <p className="text-white/60 text-center max-w-[200px]">{step.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const AccountabilitySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex items-center justify-center gap-4 flex-1 flex-wrap">
      {content.equation.map((item, i) => (
        item.type === 'operator' ? (
          <span key={i} className="text-4xl font-bold text-orange-500">{item.label}</span>
        ) : (
          <div key={i} className="bg-white/5 rounded-xl p-6 flex flex-col items-center border border-white/10 min-w-[150px]">
            <IconComponent name={item.icon} size={40} className="text-cyan-400 mb-3" />
            <span className="text-white font-semibold text-center">{item.label}</span>
            {item.sublabel && (
              <span className="text-orange-400 text-sm mt-1">{item.sublabel}</span>
            )}
          </div>
        )
      ))}
    </div>
    <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-6 border border-cyan-500/30 mt-6">
      <p className="text-xl text-white text-center">{content.message}</p>
    </div>
    <p className="text-cyan-400 text-lg italic mt-6 text-center">"{content.quote}"</p>
  </div>
);

const OurRocksSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="space-y-4 flex-1">
      {content.rocks.map((rock, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
            {i + 1}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white">{rock.title}</h3>
            <p className="text-white/60 text-sm">Owner: {rock.owner}</p>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
            rock.status === 'on-track' ? 'bg-green-500/20 text-green-400' : 
            rock.status === 'at-risk' ? 'bg-yellow-500/20 text-yellow-400' : 
            'bg-red-500/20 text-red-400'
          }`}>
            {rock.status === 'on-track' ? 'On Track' : rock.status === 'at-risk' ? 'At Risk' : 'Off Track'}
          </div>
        </div>
      ))}
    </div>
    <p className="text-cyan-400 text-lg italic mt-6">"{content.quote}"</p>
  </div>
);

const TakeawaySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="grid grid-cols-1 gap-4 flex-1 max-w-3xl mx-auto w-full">
      {content.takeaways.map((item, i) => (
        <div key={i} className="flex items-center gap-4 bg-gradient-to-r from-orange-500/20 to-orange-600/10 rounded-xl p-5 border border-orange-500/30">
          <IconComponent name={item.icon} size={28} className="text-orange-400 flex-shrink-0" />
          <span className="text-white text-lg">{item.text}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-cyan-400 text-xl italic">"{content.quote}"</p>
    </div>
  </div>
);

const CTASlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    {content.useLogo && <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-8 opacity-90" />}
    <h1 className="text-5xl md:text-7xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-7xl font-bold text-orange-500 mb-2">
      {content.highlight}
    </h1>
    <h2 className="text-3xl md:text-4xl font-light text-white/90 mb-8">
      {content.subhead}
    </h2>
    <p className="text-xl text-white/80 max-w-2xl mb-8">{content.message}</p>
    <div className="flex flex-col gap-3">
      {content.actions.map((action, i) => (
        <div key={i} className="flex items-center gap-3 text-cyan-400">
          <CheckCircle size={20} />
          <span>{action}</span>
        </div>
      ))}
    </div>
  </div>
);

// Main slide renderer
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    intro: IntroSlide,
    sixComponents: SixComponentsSlide,
    rocks: RocksSlide,
    bigRocks: BigRocksSlide,
    scorecard: ScorecardSlide,
    whyBuilt: WhyBuiltSlide,
    teamKPIs: TeamKPIsSlide,
    teamKPIs2: TeamKPIs2Slide,
    level10: Level10Slide,
    ids: IDSSlide,
    accountability: AccountabilitySlide,
    ourRocks: OurRocksSlide,
    takeaway: TakeawaySlide,
    cta: CTASlide,
  };
  
  const Renderer = renderers[slide.type];
  return Renderer ? <Renderer content={slide.content} /> : null;
};

export default function PresentationsTraction() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, toggleFullscreen, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      {/* Slide Container */}
      <div className="flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2240] to-[#0a1628]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(249, 115, 22, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 20%, rgba(34, 211, 238, 0.06) 0%, transparent 40%)
            `
          }}
        />
        
        {/* Current Slide */}
        <div className="relative h-full">
          <SlideRenderer slide={slides[currentSlide]} />
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronLeft className="text-white" size={24} />
        </button>
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronRight className="text-white" size={24} />
        </button>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#050d18] border-t border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">
            Slide {currentSlide + 1} of {slides.length}
          </span>
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentSlide ? "bg-orange-500 w-6" : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/40 text-xs">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">F</kbd> for fullscreen
            <span className="mx-2">|</span>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 ml-1">→</kbd> to navigate
          </span>
          <PresentationDownloadButton
            slides={slides}
            renderSlide={(s) => <SlideRenderer slide={s} />}
            filename="Traction"
            bgColor="#0a1628"
          />
          <button
            onClick={toggleFullscreen}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {isFullscreen ? "Exit Fullscreen" : "Present"}
          </button>
        </div>
      </div>

    </div>
  );
}
